import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import arrowLeft from '../../../static/img/darkgray16leftarrow.svg';
import arrowRight from '../../../static/img/darkgray16rightarrow.svg';
import closeIcon from '../../../static/img/white-cross12.svg';
import { htmlToPlainText } from '../utils/html';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: #0b1220;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 24px 28px;
  box-sizing: border-box;
`;

const TopBar = styled.div`
  width: 100%;
  max-width: 960px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 16px;
  background: rgba(11, 18, 32, 0.85);
  padding: 12px 16px;
  border-radius: 8px;
  backdrop-filter: blur(8px);
`;

const PageLabel = styled.div`
  grid-column: 2;
  color: #fff;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 500;
`;

const CloseButton = styled.button`
  grid-column: 3;
  justify-self: end;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  img {
    display: block;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Stage = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 1100px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  position: relative;
`;

const NavButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  padding: 0;

  img {
    display: block;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: #f3f4f6;
  }
`;

const PreviewFrame = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  max-height: calc(100vh - 220px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
`;

const PreviewText = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px;
  overflow: auto;
  box-sizing: border-box;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  line-height: 1.5;
  color: #1e252a;
  white-space: pre-wrap;
  text-align: left;
`;

const Footer = styled.div`
  width: 100%;
  max-width: 960px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
`;

const DocTitle = styled.div`
  color: #fff;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 500;
`;

const ThumbRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ThumbButton = styled.button`
  width: 56px;
  height: 72px;
  padding: 0;
  border-radius: 4px;
  border: 2px solid ${({ $active }) => ($active ? '#7200e0' : 'transparent')};
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  box-sizing: border-box;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ThumbText = styled.span`
  display: block;
  width: 100%;
  height: 100%;
  padding: 4px;
  box-sizing: border-box;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 7px;
  line-height: 1.2;
  color: #4b464f;
  overflow: hidden;
  text-align: left;
`;

export const BulkUploadPageGallery = ({
  pages = [],
  initialPageId,
  onClose,
}) => {
  const initialIndex = Math.max(
    0,
    pages.findIndex((page) => page.id === initialPageId),
  );
  const [index, setIndex] = useState(initialIndex);
  const active = pages[index];

  useEffect(() => {
    setIndex(
      Math.max(
        0,
        pages.findIndex((page) => page.id === initialPageId),
      ),
    );
  }, [initialPageId, pages]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(pages.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, pages.length]);

  if (!active || typeof document === 'undefined') return null;

  const isManualPage = active.source === 'manual' && active.content;
  const manualPagesCount = pages.filter(p => p.source === 'manual').length;
  const imagePagesCount = pages.filter(p => p.source !== 'manual').length;

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label="Page gallery preview"
      data-component="bulk-upload-gallery"
      data-testid="bulk-upload-page-gallery"
      data-total-pages={pages.length}
      data-current-index={index}
      data-current-page-number={active.pageNumber ?? index + 1}
      data-is-manual-page={isManualPage}
      data-manual-pages-count={manualPagesCount}
      data-image-pages-count={imagePagesCount}
    >
      <TopBar>
        <span />
        <PageLabel>Page {active.pageNumber ?? index + 1}</PageLabel>
        <CloseButton type="button" aria-label="Close gallery" onClick={onClose}>
          <img src={closeIcon} alt="" width={14} height={14} />
        </CloseButton>
      </TopBar>

      <Stage>
        <NavButton
          type="button"
          aria-label="Previous page"
          disabled={index <= 0}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
        >
          <img src={arrowLeft} alt="" width={16} height={16} />
        </NavButton>

        <PreviewFrame>
          {active.source === 'manual' && active.content ? (
            <PreviewText>{htmlToPlainText(active.content)}</PreviewText>
          ) : (
            <PreviewImage
              src={active.thumbnail || active.preview || active.url}
              alt={`Page ${active.pageNumber ?? index + 1}`}
            />
          )}
        </PreviewFrame>

        <NavButton
          type="button"
          aria-label="Next page"
          disabled={index >= pages.length - 1}
          onClick={() => setIndex((i) => Math.min(pages.length - 1, i + 1))}
        >
          <img src={arrowRight} alt="" width={16} height={16} />
        </NavButton>
      </Stage>

      <Footer>
        <DocTitle>{active.label || `Page ${active.pageNumber ?? index + 1}`}</DocTitle>
        {pages.length > 1 && (
          <ThumbRow>
            {pages.map((page, i) => (
              <ThumbButton
                key={page.id}
                type="button"
                $active={i === index}
                aria-label={`Go to page ${page.pageNumber ?? i + 1}`}
                onClick={() => setIndex(i)}
              >
                {page.source === 'manual' && page.content ? (
                  <ThumbText>{htmlToPlainText(page.content)}</ThumbText>
                ) : (
                  <img src={page.thumbnail || page.fileUrl || page.preview || page.url} alt="" />
                )}
              </ThumbButton>
            ))}
          </ThumbRow>
        )}
      </Footer>
    </Overlay>,
    document.body,
  );
};
